import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Skeleton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  PeopleOutlined,
  SchoolOutlined,
  PersonOutlined,
  MenuBookOutlined,
  EventOutlined,
  PlayCircleOutlined,
  FiberManualRecord,
  AccessTime,
  HowToReg,
  TrendingUp,
  CalendarMonth,
  Percent,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { getInitials } from '../../utils/getInitials';

interface DashboardStats {
  totalUsers: number;
  totalInstructors: number;
  totalStudents: number;
  totalAdmins: number;
  totalWorkshops: number;
  publishedWorkshops: number;
  totalSessions: number;
  activeSessions: number;
  totalHelpRequests: number;
  openHelpRequests: number;
}

interface ActiveSession {
  id: string;
  name: string;
  workshopTitle: string;
  instructorName: string;
  studentCount: number;
  startedAt: string;
  duration: number;
}

interface TrendPoint {
  date: string;
  count: number;
}

interface RecentUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  createdAt: string;
}

interface RecentSession {
  id: string;
  name: string;
  status: string;
  workshopTitle: string;
  instructorName: string;
  createdAt: string;
}

interface SystemHealth {
  database: { status: 'healthy' | 'unhealthy'; responseTimeMs: number };
  uptime: number;
  memoryUsage: { used: number; total: number };
  version: string;
}

interface DashboardData {
  stats: DashboardStats;
  recentUsers: RecentUser[];
  recentSessions: RecentSession[];
  activeSessionDetails: ActiveSession[];
  userRegistrationTrend: TrendPoint[];
  sessionTrend: TrendPoint[];
}

interface RegistrationMetrics {
  totalSelfRegistered: number;
  thisWeekCount: number;
  thisMonthCount: number;
  trend: TrendPoint[];
  topDomains: { domain: string; count: number }[];
  verificationStats: {
    totalCodesSent: number;
    totalVerified: number;
    conversionRate: number;
  };
}

const statCards = [
  { key: 'totalUsers' as const, label: 'Total Users', icon: PeopleOutlined, color: '#3b82f6' },
  { key: 'totalInstructors' as const, label: 'Instructors', icon: SchoolOutlined, color: '#8b5cf6' },
  { key: 'totalStudents' as const, label: 'Students', icon: PersonOutlined, color: '#22c55e' },
  { key: 'totalWorkshops' as const, label: 'Tech Evals', icon: MenuBookOutlined, color: '#f59e0b' },
  { key: 'totalSessions' as const, label: 'Sessions', icon: EventOutlined, color: '#14b8a6' },
  { key: 'activeSessions' as const, label: 'Active Now', icon: PlayCircleOutlined, color: '#ef4444' },
];

const AdminDashboard: React.FC = () => {
  const { showNotification } = useNotification();
  const { settings: publicSettings } = usePublicSettings();
  const [data, setData] = useState<DashboardData | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [regMetrics, setRegMetrics] = useState<RegistrationMetrics | null>(null);
  const [regMetricsLoading, setRegMetricsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, healthRes] = await Promise.all([
          adminService.getDashboardStats(),
          adminService.getSystemHealth(),
        ]);
        setData(dashRes.data);
        setHealth(healthRes.data);
      } catch {
        setError('Failed to load dashboard data');
        showNotification('Failed to load dashboard data', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchRegMetrics = async () => {
      try {
        const res = await adminService.getRegistrationMetrics();
        setRegMetrics(res.data);
      } catch {
        // Non-critical — silently skip if endpoint unavailable
      } finally {
        setRegMetricsLoading(false);
      }
    };
    fetchRegMetrics();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error">{error || 'No data'}</Alert>;
  }

  // Build activity timeline by merging recent users + sessions
  const activityItems = [
    ...data.recentUsers.map((u) => ({
      id: `u-${u.id}`,
      text: `${u.firstName} ${u.lastName} joined as ${u.role}`,
      time: u.createdAt,
      color: u.role === 'instructor' ? '#8b5cf6' : u.role === 'admin' ? '#e94560' : '#22c55e',
      initials: getInitials(u.firstName, u.lastName),
    })),
    ...data.recentSessions.map((s) => ({
      id: `s-${s.id}`,
      text: `${s.instructorName || 'Instructor'} created session "${s.name}"`,
      time: s.createdAt,
      color: '#14b8a6',
      initials: s.instructorName
        ? getInitials(s.instructorName.split(' ')[0], s.instructorName.split(' ').slice(1).join(' ') || undefined)
        : '?',
    })),
  ]
    .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
    .slice(0, 10);

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    if (days > 0) return `${days}d ${hours}h`;
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  const featureEnabled = publicSettings?.selfRegistrationEnabled ?? false;

  const renderRegistrationMetrics = () => {
    // Hide if feature disabled and no historical data
    if (!regMetricsLoading && (!regMetrics || (regMetrics.totalSelfRegistered === 0 && !featureEnabled))) {
      return null;
    }

    // Loading skeleton
    if (regMetricsLoading) {
      return (
        <Paper sx={{ p: 2.5, borderRadius: '12px', mb: 3 }} elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Skeleton width={180} height={28} />
            <Skeleton width={80} height={24} />
          </Box>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid size={{ xs: 6, md: 3 }} key={i}>
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '8px' }} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '8px' }} />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '8px' }} />
            </Grid>
          </Grid>
        </Paper>
      );
    }

    if (!regMetrics) return null;

    const regStatCards = [
      { label: 'Total Registered', value: regMetrics.totalSelfRegistered, subtitle: 'all time', icon: HowToReg, color: '#8b5cf6' },
      { label: 'This Week', value: regMetrics.thisWeekCount, subtitle: 'last 7 days', icon: TrendingUp, color: '#3b82f6' },
      { label: 'This Month', value: regMetrics.thisMonthCount, subtitle: 'last 30 days', icon: CalendarMonth, color: '#14b8a6' },
      { label: 'Conversion Rate', value: `${regMetrics.verificationStats.conversionRate}%`, subtitle: 'codes sent → registered', icon: Percent, color: '#f59e0b' },
    ];

    const maxDomainCount = regMetrics.topDomains.length > 0
      ? Math.max(...regMetrics.topDomains.map((d) => d.count))
      : 1;

    return (
      <Paper sx={{ p: 2.5, borderRadius: '12px', mb: 3, opacity: featureEnabled ? 1 : 0.7 }} elevation={0}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography variant="h6" fontWeight={600}>
            Self-Registration
          </Typography>
          <Chip
            label={featureEnabled ? 'Enabled' : 'Disabled'}
            size="small"
            sx={{
              bgcolor: featureEnabled ? '#22c55e20' : '#6b728020',
              color: featureEnabled ? '#22c55e' : '#6b7280',
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
            icon={<FiberManualRecord sx={{ fontSize: '8px !important', color: featureEnabled ? '#22c55e' : '#6b7280' }} />}
          />
        </Box>

        {!featureEnabled && regMetrics.totalSelfRegistered > 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
            Feature currently disabled. Showing historical data.
          </Alert>
        )}

        {/* Stat cards */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {regStatCards.map((card) => {
            const Icon = card.icon;
            return (
              <Grid size={{ xs: 6, md: 3 }} key={card.label}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Avatar sx={{ bgcolor: `${card.color}15`, color: card.color, width: 40, height: 40 }}>
                    <Icon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                      {card.subtitle}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Trend + Domains */}
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Registration Trend (30 days)
              </Typography>
              {regMetrics.trend.length === 0 ? (
                <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No registrations yet</Typography>
                </Box>
              ) : (
                <Box sx={{ height: 180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={regMetrics.trend}>
                      <defs>
                        <linearGradient id="regGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d) => dayjs(d).format('MMM D')}
                        tick={{ fontSize: 10, fill: '#999' }}
                        axisLine={false}
                        tickLine={false}
                        interval={4}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#999' }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                      />
                      <RechartsTooltip
                        labelFormatter={(d) => dayjs(d).format('MMM D, YYYY')}
                        formatter={(value) => [`${value} registration${value !== 1 ? 's' : ''}`, '']}
                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#8b5cf6"
                        fill="url(#regGrad)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Top Email Domains
              </Typography>
              {regMetrics.topDomains.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 150 }}>
                  <Typography variant="body2" color="text.secondary">No registrations yet</Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {regMetrics.topDomains.slice(0, 10).map((d) => (
                    <ListItem key={d.domain} disableGutters sx={{ px: 0, py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {d.domain}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                              {d.count}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <LinearProgress
                            variant="determinate"
                            value={(d.count / maxDomainCount) * 100}
                            sx={{
                              mt: 0.5,
                              height: 4,
                              borderRadius: 2,
                              bgcolor: 'action.hover',
                              '& .MuiLinearProgress-bar': { bgcolor: '#8b5cf6', borderRadius: 2 },
                            }}
                          />
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <Box>
      {/* Row 1: Stats Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {statCards.map((card) => {
          const Icon = card.icon;
          const value = data.stats[card.key];
          return (
            <Grid size={{ xs: 6, md: 4, xl: 2 }} key={card.key}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  borderLeft: `4px solid ${card.color}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
                elevation={0}
              >
                <Avatar
                  sx={{
                    bgcolor: `${card.color}15`,
                    color: card.color,
                    width: 48,
                    height: 48,
                  }}
                >
                  <Icon />
                </Avatar>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                      {value}
                    </Typography>
                    {card.key === 'activeSessions' && value > 0 && (
                      <FiberManualRecord
                        sx={{
                          fontSize: 10,
                          color: '#ef4444',
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.3 },
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Self-Registration Metrics */}
      {renderRegistrationMetrics()}

      {/* Row 2: Charts */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2.5, borderRadius: '12px' }} elevation={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              User Registrations (30 days)
            </Typography>
            <Box sx={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.userRegistrationTrend}>
                  <defs>
                    <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => dayjs(d).format('MMM D')}
                    tick={{ fontSize: 11, fill: '#999' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#999' }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    labelFormatter={(d) => dayjs(d).format('MMM D, YYYY')}
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#3b82f6"
                    fill="url(#userGrad)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2.5, borderRadius: '12px' }} elevation={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Sessions Created (30 days)
            </Typography>
            <Box sx={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.sessionTrend}>
                  <defs>
                    <linearGradient id="sessGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => dayjs(d).format('MMM D')}
                    tick={{ fontSize: 11, fill: '#999' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#999' }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    labelFormatter={(d) => dayjs(d).format('MMM D, YYYY')}
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#14b8a6"
                    fill="url(#sessGrad)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Row 3: Active Sessions + Recent Activity */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2.5, borderRadius: '12px', minHeight: 340 }} elevation={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Active Sessions
            </Typography>
            {data.activeSessionDetails.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 6,
                  color: 'text.secondary',
                }}
              >
                <EventOutlined sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2">No active sessions</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {data.activeSessionDetails.map((session) => {
                  const elapsed = session.startedAt
                    ? Math.floor((Date.now() - new Date(session.startedAt).getTime()) / 60000)
                    : 0;
                  return (
                    <Paper
                      key={session.id}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: '8px' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <FiberManualRecord sx={{ fontSize: 8, color: '#22c55e' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }} noWrap>
                          {session.workshopTitle}
                        </Typography>
                        <Chip
                          label={`${session.studentCount} students`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {session.name} &middot; {session.instructorName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {elapsed} min elapsed / {session.duration} min planned
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2.5, borderRadius: '12px', minHeight: 340 }} elevation={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Recent Activity
            </Typography>
            {activityItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No recent activity
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {activityItems.map((item) => (
                  <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: item.color,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}
                    >
                      {item.initials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {item.text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(item.time).fromNow()}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row 4: System Health */}
      {health && (
        <Paper sx={{ p: 2, borderRadius: '12px' }} elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FiberManualRecord
                sx={{
                  fontSize: 10,
                  color: health.database.status === 'healthy' ? '#22c55e' : '#ef4444',
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Database: {health.database.status === 'healthy' ? 'Healthy' : 'Unhealthy'} ({health.database.responseTimeMs}ms)
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Uptime: {formatUptime(health.uptime)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Memory: {health.memoryUsage.used} MB / {health.memoryUsage.total} MB
            </Typography>
            <Typography variant="body2" color="text.secondary">
              v{health.version}
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AdminDashboard;
